import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Edit, Trash2, Upload, Download } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QuestionDialog } from "./QuestionDialog";
import { CSVImportDialog } from "./csv/CSVImportDialog";
import { CSVExportDialog } from "./csv/CSVExportDialog";
import { useSearchParams } from "react-router-dom";
import { MultiSelectFilter, OptionItem } from "./questions/MultiSelectFilter";
import { InlineEditableText } from "./questions/InlineEditableText";
import { TranslationStatus } from "./questions/TranslationStatus";
interface Question {
  id: string;
  question_text: string;
  question_text_i18n: Record<string, string>;
  question_type_id: string;
  is_required: boolean;
  is_reverse_scored: boolean;
  order_index: number;
  tool_id: string;
  tools: {
    name: string;
  } | null;
  question_types: {
    name: string;
  } | null;
  question_focus_areas: Array<{
    focus_areas: {
      id: string;
      name: string;
    } | null;
  }>;
  survey_template_questions?: Array<{
    survey_templates: {
      id: string;
      name: string;
      is_featured: boolean;
    };
  }>;
}

export const QuestionsManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);

  // Filters (URL persisted)
  const [typeFilter, setTypeFilter] = useState<string[]>(searchParams.get("qt")?.split(",").filter(Boolean) || []);
  const [toolFilter, setToolFilter] = useState<string[]>(searchParams.get("tool")?.split(",").filter(Boolean) || []);
  const [templateFilter, setTemplateFilter] = useState<string[]>(searchParams.get("tmpl")?.split(",").filter(Boolean) || []);
  const [focusAreaFilter, setFocusAreaFilter] = useState<string[]>(searchParams.get("fa")?.split(",").filter(Boolean) || []);

  // Selection and pagination
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState<number>(parseInt(searchParams.get("page") || "1", 10));
  const pageSize = 25;

  // Options for filters
  const { data: questionTypes } = useQuery({
    queryKey: ["question-types-filter"],
    queryFn: async () => {
      const { data, error } = await supabase.from("question_types").select("id, name").order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const { data: tools } = useQuery({
    queryKey: ["tools-filter"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tools").select("id, name").eq("is_active", true).order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const { data: templates } = useQuery({
    queryKey: ["survey-templates-filter"],
    queryFn: async () => {
      const { data, error } = await supabase.from("survey_templates").select("id, name").order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const { data: focusAreas } = useQuery({
    queryKey: ["focus-areas-filter"],
    queryFn: async () => {
      const { data, error } = await supabase.from("focus_areas").select("id, name").order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const { data: qResult, isLoading, refetch } = useQuery({
    queryKey: ['admin-questions', { searchTerm, typeFilter, toolFilter, templateFilter, focusAreaFilter, page }],
    queryFn: async () => {
      // Resolve filtered question IDs via relationships if needed
      let filteredIds: string[] | null = null;

      if (focusAreaFilter.length > 0) {
        const { data, error } = await supabase
          .from('question_focus_areas')
          .select('question_id')
          .in('focus_area_id', focusAreaFilter);
        if (error) throw error;
        filteredIds = Array.from(new Set((data || []).map((d: any) => d.question_id)));
      }

      if (templateFilter.length > 0) {
        const { data, error } = await supabase
          .from('survey_template_questions')
          .select('question_id')
          .in('template_id', templateFilter);
        if (error) throw error;
        const idsFromTemplates = Array.from(new Set((data || []).map((d: any) => d.question_id)));
        filteredIds = filteredIds
          ? filteredIds.filter((id) => idsFromTemplates.includes(id))
          : idsFromTemplates;
      }

      // Base query with pagination and count
      let query = supabase
        .from('questions')
        .select('id, question_text, question_text_i18n, question_type_id, is_required, is_reverse_scored, order_index, tool_id, options', { count: 'exact' });

      if (typeFilter.length) query = query.in('question_type_id', typeFilter);
      if (toolFilter.length) query = query.in('tool_id', toolFilter);
      if (filteredIds && filteredIds.length) query = query.in('id', filteredIds);
      if (searchTerm) query = query.ilike('question_text', `%${searchTerm}%`);

      query = query.order('order_index').range((page - 1) * pageSize, page * pageSize - 1);

      const { data: questionsData, error: questionsError, count } = await query;
      if (questionsError) throw questionsError;

      const ids = (questionsData || []).map((q: any) => q.id);

      const [toolsData, questionTypesData, focusAreasData, templateData] = await Promise.all([
        supabase.from('tools').select('id, name'),
        supabase.from('question_types').select('id, name'),
        ids.length
          ? supabase.from('question_focus_areas').select('question_id, focus_areas(id, name)').in('question_id', ids)
          : Promise.resolve({ data: [], error: null } as any),
        ids.length
          ? supabase.from('survey_template_questions').select('question_id, survey_templates(id, name, is_featured)').in('question_id', ids)
          : Promise.resolve({ data: [], error: null } as any),
      ]);

      const toolsMap = new Map((toolsData.data || []).map((t: any) => [t.id, t]));
      const questionTypesMap = new Map((questionTypesData.data || []).map((qt: any) => [qt.id, qt]));

      const focusAreasMap = new Map<string, any[]>();
      (focusAreasData.data || []).forEach((qfa: any) => {
        if (!focusAreasMap.has(qfa.question_id)) focusAreasMap.set(qfa.question_id, []);
        focusAreasMap.get(qfa.question_id)!.push({ focus_areas: qfa.focus_areas });
      });

      const templatesMap = new Map<string, any[]>();
      (templateData.data || []).forEach((stq: any) => {
        if (!templatesMap.has(stq.question_id)) templatesMap.set(stq.question_id, []);
        templatesMap.get(stq.question_id)!.push({ survey_templates: stq.survey_templates });
      });

      const rows = (questionsData || []).map((question: any) => ({
        ...question,
        tools: toolsMap.get(question.tool_id) || null,
        question_types: questionTypesMap.get(question.question_type_id) || null,
        question_focus_areas: focusAreasMap.get(question.id) || [],
        survey_template_questions: templatesMap.get(question.id) || [],
      }));

      return { rows, total: count || 0 };
    },
  });

  const questions = qResult?.rows || [];
  const total = qResult?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const typeOptions: OptionItem[] = (questionTypes || []).map((t) => ({ label: t.name, value: t.id }));
  const toolOptions: OptionItem[] = (tools || []).map((t) => ({ label: t.name, value: t.id }));
  const templateOptions: OptionItem[] = (templates || []).map((t) => ({ label: t.name, value: t.id }));
  const focusAreaOptions: OptionItem[] = (focusAreas || []).map((t) => ({ label: t.name, value: t.id }));

  // Persist filters & pagination to URL
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchTerm) params.q = searchTerm;
    if (typeFilter.length) params.qt = typeFilter.join(',');
    if (toolFilter.length) params.tool = toolFilter.join(',');
    if (templateFilter.length) params.tmpl = templateFilter.join(',');
    if (focusAreaFilter.length) params.fa = focusAreaFilter.join(',');
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [searchTerm, typeFilter, toolFilter, templateFilter, focusAreaFilter, page, setSearchParams]);

  const clearAllFilters = () => {
    setTypeFilter([]);
    setToolFilter([]);
    setTemplateFilter([]);
    setFocusAreaFilter([]);
    setPage(1);
  };

  const handleEdit = (question: Question) => {
    setSelectedQuestion(question);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedQuestion(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (questionId: string) => {
    if (!confirm("Are you sure you want to delete this question?")) return;

    try {
      const { error } = await supabase
        .from('questions')
        .delete()
        .eq('id', questionId);

      if (error) throw error;
      refetch();
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search questions, tools, or focus areas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setIsImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button variant="outline" onClick={() => setIsExportOpen(true)}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add Question
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{questions?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Required Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {questions?.filter(q => q.is_required).length || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Reverse Scored</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {questions?.filter(q => q.is_reverse_scored).length || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <MultiSelectFilter label="Type" options={typeOptions} values={typeFilter} onChange={(v) => { setTypeFilter(v); setPage(1); }} />
        <MultiSelectFilter label="Tool" options={toolOptions} values={toolFilter} onChange={(v) => { setToolFilter(v); setPage(1); }} />
        <MultiSelectFilter label="Template" options={templateOptions} values={templateFilter} onChange={(v) => { setTemplateFilter(v); setPage(1); }} />
        <MultiSelectFilter label="Focus Areas" options={focusAreaOptions} values={focusAreaFilter} onChange={(v) => { setFocusAreaFilter(v); setPage(1); }} />
        <Button variant="ghost" size="sm" onClick={clearAllFilters}>Clear filters</Button>
      </div>

      {/* Questions table */}
      <Card>
        <CardHeader>
          <CardTitle>Questions ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead className="max-w-xs">Question</TableHead>
                <TableHead>Translations</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Tool</TableHead>
                <TableHead>Focus Areas</TableHead>
                <TableHead>Survey Templates</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Reverse Scored</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questions.map((question) => (
                <TableRow key={question.id} onClick={() => handleEdit(question)} className="cursor-pointer hover:bg-muted/50">
                  <TableCell className="font-medium">
                    {question.order_index}
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="max-h-16 overflow-hidden">
                      <InlineEditableText
                        value={question.question_text}
                        placeholder="Edit question text"
                        onSave={async (next) => {
                          const { error } = await supabase
                            .from('questions')
                            .update({ question_text: next })
                            .eq('id', question.id);
                          if (!error) refetch();
                        }}
                        className="text-sm leading-snug"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <TranslationStatus translations={question.question_text_i18n || {}} />
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {question.question_types?.name}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {question.tools?.name || 'No Tool'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {question.question_focus_areas.map((qfa, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {qfa.focus_areas?.name || 'Unknown'}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {question.survey_template_questions?.map((stq, index) => (
                        <Badge 
                          key={index} 
                          variant={stq.survey_templates.is_featured ? "default" : "secondary"} 
                          className="text-xs"
                        >
                          {stq.survey_templates.name}
                        </Badge>
                      )) || <span className="text-muted-foreground text-sm">None</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={question.is_required ? "default" : "secondary"}>
                      {question.is_required ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={question.is_reverse_scored ? "destructive" : "secondary"}>
                      {question.is_reverse_scored ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleEdit(question); }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDelete(question.id); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">Page {page} of {totalPages} • {total} total</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <QuestionDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        question={selectedQuestion}
        onSuccess={() => {
          refetch();
          setIsDialogOpen(false);
        }}
      />

      <CSVImportDialog
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        entity="questions"
        onSuccess={() => refetch()}
      />
      <CSVExportDialog
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        entity="questions"
      />
    </div>
  );
};